<template>
  <teleport to="body">
    <overlay
      ref="overlay"
      key="size-guide-drawer"
      block-class="size-guide-drawer"
      direction="left"
      namespace="sizeGuideDrawer"
      type="drawer"
      :overlay-label="$string('product.form.size_guide')"
    >
      <template #body>
        <!-- Tabs to toggle between views -->
        <div
          class="size-guide-drawer__view-toggle flex-override"
          role="tablist"
        >
          <btn
            id="size_guide_sizes_tab_button"
            ref="sizesButton"
            class="size-guide-drawer__view-toggle-button"
            :class="{
              'active': activeView === 'sizes',
            }"
            modifiers="primary outline block center"
            label-class="text-body-2xs-regular"
            :label="$string('product.size_guide.size_guide')"
            role="tab"
            aria-controls="size_guide_sizes_tab"
            @keyup.right="setActiveView('conversion')"
            @click="setActiveView('sizes')"
          />

          <btn
            id="size_guide_conversion_tab_button"
            ref="conversionButton"
            class="size-guide-drawer__view-toggle-button"
            :class="{
              'active': activeView === 'conversion',
            }"
            modifiers="primary outline block center"
            label-class="text-body-2xs-regular"
            :label="$string('product.size_guide.size_conversion')"
            role="tab"
            aria-controls="size_guide_conversion_tab"
            @keyup.left="setActiveView('sizes')"
            @click="setActiveView('conversion')"
          />
        </div>

        <div role="tabpanel">
          <div
            v-show="activeView === 'sizes'"
            id="size_guide_sizes_tab"
            class="size-guide-drawer__fit-notes-wrapper"
            :aria-selected="activeView === 'sizes'"
            aria-labelledby="size_guide_sizes_tab_button"
            :tabindex="activeView === 'sizes' ? 0 : -1"
          >
            <p
              ref="copy"
              class="size-guide-drawer__copy text-body-2xs-regular"
              v-html="drawerCopy"
            />

            <div
              v-if="product?.metafields?.fitNotes || product?.metafields?.fitGraphicScale"
              class="size-guide-drawer__fit-notes"
              :class="{ 'no-fit-graphics': !isSetFitGraphic() }"
            >
              <h2
                class="size-guide-drawer__fit-notes-title"
                v-text="`${$string('product.size_guide.fit_notes')}: ${ product.title }`"
              />

              <fit-notes
                :product-fit-notes="product?.metafields?.fitNotes || null"
                :product-fit-graphic="{
                  overallFit: product?.metafields?.fitGraphicOverallFit,
                  scale: product?.metafields?.fitGraphicScale,
                  low: product?.metafields?.fitGraphicLow,
                  middle: product?.metafields?.fitGraphicMiddle,
                  high: product?.metafields?.fitGraphicHigh
                }"
              />
            </div>
          </div>

          <div
            v-if="sizeGuideSettings?.sizeConversionCopy"
            v-show="activeView === 'conversion'"
            id="size_guide_conversion_tab"
            class="size-guide-drawer__copy text-body-2xs-regular"
            :aria-selected="activeView === 'conversion'"
            aria-labelledby="size_guide_conversion_tab_button"
            :tabindex="activeView === 'conversion' ? 0 : -1"
            v-html="sizeGuideSettings?.sizeConversionCopy"
          />
        </div>

        <div class="size-guide-drawer__chart">
          <size-guide
            is-drawer
            :product-type="productType"
            :size-guide-page-url="sizeGuideSettings?.page"
            :is-conversion-guide="activeView === 'conversion'"
            :conversion-guide="sizeGuideSettings?.internationalConversionGuide"
            :iso-code="isoCode"
          />
        </div>

        <div class="size-guide-drawer__how-to-measure">
          <how-to-measure
            :copy="sizeGuideSettings?.howToMeasureCopy"
            :image="sizeGuideSettings?.howToMeasureImage"
          />
        </div>
      </template>
    </overlay>
  </teleport>
</template>

<script>

/**
 * Vue: Size guide drawer (size-guide-drawer)
 * -----------------------------------------------------------------------------
 * Overlay that houses the size guide drawer associated with a product.
 *
 */
import FitNotes from '~async/fit-notes/fit-notes'
import HowToMeasure from '~async/how-to-measure/how-to-measure'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import SizeGuide from './size-guide'

import { mapState } from 'vuex'

export default {
  name: 'SizeGuideDrawer',

  components: {
    Btn,
    FitNotes,
    HowToMeasure,
    Overlay,
    SizeGuide,
  },

  props: {
    sizeGuideSettings: {
      type: Object,
      default: () => ({}),
    },
    isoCode: {
      type: String,
      default: 'US',
    },
  },

  data() {
    return {
      activeView: 'sizes',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      product: (state) => state.sizeGuideDrawer.product,
    }),

    /**
     * Formats drawer copy string to add HTML link to how to measure guide.
     * @returns {String}
     */
    drawerCopy() {
      const copy = this.$string('product.size_guide.size_guide_copy')
      const howToMeasureString = /how to measure/i
      const match = copy?.match(howToMeasureString)

      return copy?.replace(match, `<a href="#to_bottom">${match}</a>`)
    },

    /**
     * Compute product type to pass into size-guide.
     * @returns {String|null}
     */
    productType() {
      return this.product && this.product?.type
        ? this.product.type
        : null
    },
  },

  mounted() {
    this.setEventListeners()
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {
      // Scroll to bottom of drawer when 'how to measure' text is clicked.
      const copy = this.$refs.copy
      const copyLink = copy?.querySelector('a[href="#to_bottom"]')

      if (!copyLink) {
        return
      }

      copyLink.addEventListener('click', ($event) => {
        $event.preventDefault()
        this.scrollToBottom()
      })
    },

    /**
     * Scroll to bottom of drawer.
     */
    scrollToBottom() {
      const scrollElement = this.$refs?.overlay?.scrollElement

      scrollElement?.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth',
      })
    },

    /**
     * Set which view is active between 'sizes' and 'conversion'.
     */
    setActiveView(view) {
      this.activeView = view
      this.$refs[`${view}Button`]?.focus()
    },

    /**
     * Checks if fit graphics are added
     */
    isSetFitGraphic() {
      return this.product?.metafields?.fitGraphicOverallFit ||
        this.product?.metafields?.fitGraphicScale ||
        this.product?.metafields?.fitGraphicLow ||
        this.product?.metafields?.fitGraphicMiddle ||
        this.product?.metafields?.fitGraphicHigh
    },
  },
}
</script>
